import isNull from 'lodash/isNull';
import assign from 'lodash/assign';
import merge from 'lodash/merge';
import isNumber from 'lodash/isNumber';
import isUndefined from 'lodash/isUndefined';
import minBy from 'lodash/minBy';
import {
  checkGVP,
  filterExistingProperties,
  isBundlePriceEnabled,
  sunRule,
} from '.';
import { parameterToPlainAdapter } from '../../adapters/ParameterToPlainAdapter';
import { LensPackage } from '../../meta/IBrain';
// total prices
const getDiscountInsurance = (
  listPrice: number,
  discount: number,
  insPrice: number
) => {
  let discountInsurance = 0;

  if (insPrice) {
    discountInsurance = listPrice - insPrice;
  }
  return discountInsurance;
};
const getPrices = (
  list: string | number,
  offer: string | number,
  ins: string | number,
  defaultList?: string | number
) => {
  let listPrice = castNumber(list);
  let offerPrice = castNumber(offer);
  let insPrice = castNumber(ins);

  if (isUndefined(offerPrice) || isNull(offerPrice)) {
    let def = castNumber(defaultList);
    if (isUndefined(def) || isNull(def)) {
      offerPrice = 0;
    } else {
      offerPrice = def;
    }
  }

  if (isUndefined(listPrice) || isNull(listPrice)) {
    listPrice = 0;
  }

  /*  if (isUndefined(insPrice) || isNull(insPrice)) {
                insPrice = 0;
            } */

  return {
    price: listPrice,
    discount: listPrice - offerPrice,
    insPrice: !isUndefined(insPrice) && !isNull(insPrice) ? insPrice : null,
  };
};
const castNumber = (p: string | number) => {
  let pNumber = null;

  if (!isNumber(p)) {
    if (!p) {
      pNumber = null;
    } else {
      pNumber = parseFloat(p.toString());
    }
  } else {
    pNumber = p;
  }
  return pNumber;
};

function useNonGVPTotalPrices(currentPackages: LensPackage[]) {
  const configFrame = window.rxcBrain?.dataFrame;
  const currentTreatments = window.rxcBrain?.currentTreatments;
  const currentStep = window.rxcBrain?.currentStep;

  const insuranceEnabled = window.rxcBrain?.insurance?.insuranceEnabled;

  const sessionWarranty = window.rxcBrain?.selectedWarranty;

  const additionalParameters = window.rxcBrain?.additionalParameters;

  const cartMode = window.rxcBrain?.config?.cartMode;

  const warrantyByCart = window.rxcBrain?.config?.data?.warranty?.catEntryId;

  const warrantyOptions = window.rxcBrain?.config?.lensesData?.warrantyOptions;
  const bundlesEnabled = window.rxcBrain?.bundlesEnabled;
  const showFramePlusLensesPrice = window.rxcBrain?.showFramePlusLensesPrice;

  let priceObj = {
    frame: getPrices(
      configFrame?.listPrice,
      configFrame?.offerPrice,
      configFrame?.insPrice
    ),
    lens: { price: 0, discount: 0, insPrice: 0 },
    total: getPrices(
      configFrame?.listPrice,
      configFrame?.offerPrice,
      configFrame?.insPrice
    ),
  };

  let packages = currentPackages;

  if (currentStep && currentTreatments) {
    let params = {};
    //CM fix set treatment family back
    //filter by treatment if navigation is > of treatment step
    if (
      currentStep.progressive >
      window.rxcBrain?.findByStepKey('LensTreatment').progressive
    ) {
      currentTreatments.forEach((p: any) => {
        let mapped = parameterToPlainAdapter.adapt(p);
        if (mapped) {
          if (currentPackages.length) {
            if (currentPackages[0].frame.promoType === 'GVP') {
              let newMapped = {};
              Object.keys(mapped).map((treatmentKey) => {
                newMapped['treatment'] = mapped[treatmentKey];
              });
              mapped = newMapped;
            }
            params = { ...params, ...mapped };
          }
        }
      });
    }
    packages = packages.filter((s) =>
      filterExistingProperties(s.lensPackage, params)
    );
  }

  let cheapPackage = null;

  if (additionalParameters) {
    packages = packages.filter((s) =>
      filterExistingProperties(s.lensPackage, additionalParameters)
    );
  }

  //If it's the first step in which we're making a selection, then we still didn't pick a lens
  if (
    !window.rxcBrain?.firstStepWithManualSelection ||
    (window.rxcBrain?.firstStepWithManualSelection &&
      window.rxcBrain?.firstStepWithManualSelection.progressive >=
        currentStep?.progressive)
  ) {
    cheapPackage = getCheapPackageFromList(currentPackages);
  } else {
    if (insuranceEnabled) {
      cheapPackage = getCheapInsurancePackageFromList(packages);
    } else {
      cheapPackage = getCheapPackageFromList(packages);
    }
  }
  // filtering packages landing on lensColor step taking only valorized color packages
  // after clicking on sun tile
  if (
    window.rxcBrain
      ?.findByStepKey('LensColour')
      ?.params?.find(
        (param: any) =>
          typeof param?.value === 'string' &&
          param?.value?.toLowerCase() === 'sun'
      )
  ) {
    const colorPackages = currentPackages.filter(
      (cp: any) => cp?.lensPackage?.color && cp?.lensPackage?.color !== ''
    );
    cheapPackage = getCheapPackageFromList(colorPackages);
  }

  if (cheapPackage && configFrame) {
    let frame = getPrices(
      cheapPackage?.frame?.listPrice || configFrame?.listPrice,
      cheapPackage?.frame?.offerPrice || configFrame?.offerPrice,
      insuranceEnabled
        ? configFrame?.insPrice
        : cheapPackage?.frame?.insPrice || configFrame?.insPrice,
      configFrame?.offerPrice
    );
    let lens = getPrices(
      cheapPackage?.lensPackage?.listPrice,
      cheapPackage?.lensPackage?.offerPrice,
      cheapPackage?.lensPackage?.insPrice
    );

    //it can happen that we don't have a plano package, so we should just set the lens price to 0
    //while we still didn't make a manual selection yet
    if (
      (!window.rxcBrain?.firstStepWithManualSelection &&
        !bundlesEnabled &&
        !showFramePlusLensesPrice) ||
      (window.rxcBrain?.firstStepWithManualSelection &&
        window.rxcBrain?.firstStepWithManualSelection.progressive >=
          currentStep?.progressive &&
        !bundlesEnabled &&
        !showFramePlusLensesPrice)
    ) {
      lens = { price: 0, discount: 0, insPrice: null };
    }

    if (insuranceEnabled && window.rxcBrain?.isLastStep()) {
      frame.discount = getDiscountInsurance(
        frame.price,
        frame.discount,
        cheapPackage?.frame?.insPrice
      );
      lens.discount = getDiscountInsurance(
        lens.price,
        lens.discount,
        cheapPackage?.lensPackage?.insPrice
      );
    }

    // in case of bundles (if not frame only) dont't take in count the frame price

    if (bundlesEnabled) {
      if (
        cheapPackage?.lensPackage?.listPrice ===
        cheapPackage?.lensPackage?.offerPrice
      ) {
        // no promo applied to bundles
        lens = {
          price:
            parseFloat(cheapPackage?.lensPackage?.originalListPrice) +
            parseFloat(cheapPackage?.frame?.offerPrice),
          discount:
            parseFloat(cheapPackage?.lensPackage?.originalListPrice) +
            parseFloat(cheapPackage?.frame?.offerPrice) -
            parseFloat(cheapPackage?.lensPackage?.listPrice),
          insPrice: parseFloat(cheapPackage?.lensPackage?.insPrice),
        };
      }
      frame = { price: 0, discount: 0, insPrice: null };

      if (cheapPackage.lensPackage.type === 'Frame Only') {
        lens = { price: 0, discount: 0, insPrice: null };
        frame = getPrices(
          cheapPackage?.frame?.listPrice || configFrame?.listPrice,
          cheapPackage?.frame?.offerPrice || configFrame?.offerPrice,
          cheapPackage?.frame?.insPrice || configFrame?.insPrice,
          configFrame?.offerPrice
        );
      }
    }

    const total = {
      price: frame.price + lens.price,
      discount: frame.discount + lens.discount,
      insPrice:
        /*frame.insPrice !== null || lens.insPrice !== null
          ? */ (frame?.insPrice ?? frame?.price - frame?.discount ?? 0) +
        (lens?.insPrice ?? lens?.price - lens.discount ?? 0),
      //: null,
    };

    if (sessionWarranty) {
      const warrantyPrice = getPrices(
        sessionWarranty.price,
        sessionWarranty.price,
        0
      );
      total.price += warrantyPrice.price;
      lens.price += warrantyPrice.price;
      lens.insPrice += warrantyPrice.price;
      total.insPrice += warrantyPrice.price;
    } else if (cartMode) {
      if (warrantyByCart && sessionWarranty) {
        const currentWarranty = warrantyOptions?.find(
          (warranty) => warranty.id === warrantyByCart
        );
        const warrantyPrice = getPrices(
          currentWarranty.price,
          currentWarranty.price,
          0
        );
        total.price += warrantyPrice.price;
        lens.price += warrantyPrice.price;
      }
    }

    priceObj = {
      frame,
      lens,
      total,
    };
  }

  return priceObj;
}

function useGVPTotalPrices(currentPackages: LensPackage[]) {
  const configFrame = window.rxcBrain?.dataFrame;
  const currentTreatments = window.rxcBrain?.currentTreatments;
  const currentStep = window.rxcBrain?.currentStep;

  const insuranceEnabled = window.rxcBrain?.insuranceEnabled;

  const sessionWarranty = window.rxcBrain?.selectedWarranty;

  const configPackages = window.rxcBrain?.initialLensPackages;

  const additionalParameters = window.rxcBrain?.additionalParameters;

  let priceObj = {
    frame: !currentTreatments
      ? getPrices(configFrame?.listPrice, configFrame?.offerPrice, 0)
      : {
          price: 0,
          discount: 0,
          insPrice: 0,
        },
    lens: { price: 0, discount: 0, insPrice: 0 },
    total: !currentTreatments
      ? getPrices(configFrame?.listPrice, configFrame?.offerPrice, 0)
      : {
          price: 0,
          discount: 0,
          insPrice: 0,
        },
  };

  let packages = currentPackages;

  if (currentStep && currentTreatments) {
    let params = {};
    //CM fix set treatment family back
    //filter by treatment if navigation is > of treatment step
    if (
      currentStep.progressive >
      window.rxcBrain?.findByStepKey('LensTreatment').progressive
    ) {
      currentTreatments.forEach((p: any) => {
        let mapped = parameterToPlainAdapter.adapt(p);
        if (mapped) {
          if (currentPackages.length) {
            if (currentPackages[0].frame.promoType === 'GVP') {
              let newMapped = {};
              Object.keys(mapped).map((treatmentKey) => {
                newMapped['treatment'] = mapped[treatmentKey];
              });
              mapped = newMapped;
            }
            params = { ...params, ...mapped };
          }
        }
      });
    }

    packages = packages.filter((s) =>
      filterExistingProperties(s.lensPackage, params)
    );
  }

  let cheapPackage = null;

  if (additionalParameters) {
    packages = packages.filter((s) =>
      filterExistingProperties(s.lensPackage, additionalParameters)
    );
  }

  //If it's the first step in which we're making a selection, then we still didn't pick a lens
  if (
    !window.rxcBrain?.firstStepWithManualSelection ||
    (window.rxcBrain?.firstStepWithManualSelection &&
      window.rxcBrain?.firstStepWithManualSelection.progressive >=
        currentStep?.progressive)
  ) {
    cheapPackage = getCheapPackageFromList(configPackages);
  } else {
    if (insuranceEnabled) {
      cheapPackage = getCheapInsurancePackageFromList(packages);
    } else {
      cheapPackage = getCheapPackageFromList(packages);
    }
  }

  if (cheapPackage) {
    const frame =
      !window.rxcBrain?.firstStepWithManualSelection ||
      (window.rxcBrain?.firstStepWithManualSelection &&
        window.rxcBrain?.firstStepWithManualSelection.progressive >=
          currentStep?.progressive)
        ? getPrices(
            cheapPackage?.frame?.listPrice || configFrame?.listPrice,
            cheapPackage?.frame?.offerPrice || configFrame?.offerPrice,
            cheapPackage?.frame?.insPrice || configFrame?.insPrice,
            configFrame?.offerPrice
          )
        : { price: 0, discount: 0, insPrice: null };

    let lens =
      !window.rxcBrain?.firstStepWithManualSelection ||
      (window.rxcBrain?.firstStepWithManualSelection &&
        window.rxcBrain?.firstStepWithManualSelection.progressive >=
          currentStep?.progressive)
        ? { price: 0, discount: 0, insPrice: null }
        : getPrices(
            cheapPackage?.lensPackage?.listPrice,
            cheapPackage?.lensPackage?.offerPrice,
            cheapPackage?.lensPackage?.insPrice
          );

    if (insuranceEnabled && window.rxcBrain?.isLastStep()) {
      frame.discount = getDiscountInsurance(
        frame.price,
        frame.discount,
        cheapPackage?.frame?.insPrice
      );
      lens.discount = getDiscountInsurance(
        lens.price,
        lens.discount,
        cheapPackage?.lensPackage?.insPrice
      );
    }

    /* if (!frame.insPrice && frame.insPrice !== 0) {
                  frame.insPrice = frame.price;
              }

              if (!lens.insPrice && lens.insPrice !== 0 && insuranceEnabled) {
                  lens.insPrice = lens.price;
              } */

    let total = null;

    if (!frame.price && !frame.discount) {
      total = {
        price: parseFloat(cheapPackage.lensPackage.listPrice),
        discount:
          parseFloat(cheapPackage.lensPackage.listPrice) -
          (parseFloat(cheapPackage.lensPackage.offerPrice) +
            parseFloat(cheapPackage.frame.offerPrice)),
        insPrice: /*cheapPackage.lensPackage.insPrice !== null
            ? */ cheapPackage.lensPackage.insPrice
          ? parseFloat(cheapPackage.lensPackage.insPrice)
          : parseFloat(cheapPackage.lensPackage.offerPrice) +
            parseFloat(cheapPackage.frame.offerPrice),
        //: null,
      };
    } else {
      total = {
        price: frame.price + lens.price,
        discount: frame.discount + lens.discount,
        insPrice:
          /*frame.insPrice !== null || lens.insPrice !== null
            ? */ (frame?.insPrice ?? frame?.price - frame?.discount ?? 0) +
          (lens?.insPrice ?? lens?.price - lens?.discount ?? 0),
        //: null,
      };
    }

    if (sessionWarranty) {
      const warrantyPrice = getPrices(
        sessionWarranty.price,
        sessionWarranty.price,
        0
      );
      total.price += warrantyPrice.price;
    }

    priceObj = {
      frame,
      lens,
      total,
    };
  }

  return priceObj;
}

export function useCurrentPrices(
  currentPackages?: LensPackage[],
  config?: any
) {
  const nonGVPTotalPrices = useNonGVPTotalPrices(currentPackages);
  const GVPTotalPrices = useGVPTotalPrices(currentPackages);

  let priceObj = {
    frame: { price: 0, discount: 0, insPrice: 0 },
    lens: { price: 0, discount: 0, insPrice: 0 },
    total: { price: 0, discount: 0, insPrice: 0 },
  };

  if (
    currentPackages &&
    !checkGVP(currentPackages) &&
    !isBundlePriceEnabled(config) &&
    nonGVPTotalPrices
  ) {
    priceObj = nonGVPTotalPrices;
  } else if (
    (currentPackages && checkGVP(currentPackages) && GVPTotalPrices) ||
    isBundlePriceEnabled(config)
  ) {
    priceObj = GVPTotalPrices;
  }

  return priceObj;
}
export const noPricesInPackages = (packages) => {
  if (!packages) return null;
  const onlyLensPackages = packages.find((pkg) => pkg?.frame)
    ? packages.map((pkg) => pkg.lensPackage)
    : packages;
  return onlyLensPackages.every((pkg) => {
    const keys = Object.keys(pkg);
    return !keys.includes('offerPrice') && !keys.includes('listPrice');
  });
};
export const mergeLensesDataPrices = (packages: any, prices: any) => {
  return packages
    .filter(({ lensPackage }) => {
      return prices.find((p: any) => p.lensPackage.upc === lensPackage.upc);
    })
    .map(({ frame, lensPackage }) => {
      const pkg = prices.find(
        (p: any) => p.lensPackage.upc === lensPackage.upc
      );
      return {
        frame: {
          ...frame,
        },
        lensPackage: {
          ...assign({}, pkg?.lensPackage, lensPackage),
          offerPrice: pkg?.lensPackage.offerPrice,
          listPrice: pkg?.lensPackage.listPrice,
        },
      };
    });
};
export const getRxValues = (locale: any, rxValues: any) => {
  if (!rxValues[locale]) {
    const defaultRxValues = rxValues['en_US'];
    if (defaultRxValues) {
      return defaultRxValues;
    } else {
      throw new Error('no rxValues for en_US');
    }
  } else {
    return rxValues[locale];
  }
};
export const isPublished = (locale: any, published: any) => {
  if (!published[locale] && typeof published[locale] !== 'boolean') {
    const defaultRxValues = published['en_US'];
    if (defaultRxValues === true || defaultRxValues === false) {
      return defaultRxValues;
    } else {
      throw new Error('no published for en_US');
    }
  } else {
    return published[locale];
  }
};
////////////////////////////////////////////////////////////////

// card prices

export const getCheapestPackages = (
  packages: LensPackage[],
  filters?: Record<string, any>
) => {
  const filtered = packages.filter((s) =>
    filterExistingProperties(
      s.lensPackage,
      filters,
      s.frame?.promoType === 'GVP'
    )
  );
  if (filtered.length === 0) return null;
  const minSelectedPrice = getCheapPackageFromList(packages);
  const minPackageFilterPrice = getCheapPackageFromList(filtered);

  return {
    minSelectedPrice,
    minPackageFilterPrice,
  };
};

export const getCheapPackageFromList = (list: any): any =>
  minBy(list, (l: LensPackage) => {
    return parseFloat(l.lensPackage.listPrice);
  });

export const getCheapInsurancePackageFromList = (list: any): any =>
  minBy(list, (l: LensPackage) => {
    if (isNull(l.lensPackage.insPrice) || isUndefined(l.lensPackage.insPrice))
      return 9999.0;
    return parseFloat(l.lensPackage.insPrice);
  });

export const getPriceFromPackageAndFiltered = (
  currentPackage?: any[],
  filters?: Record<string, any>,
  isGVP?: boolean,
  showFramePlusLensesPrice?: boolean
) => {
  const toRet = {
    price: 0,
    discount: 0,
    insPrice: 0,
  };

  if (!currentPackage) return toRet;

  const coloredPackages = currentPackage.filter(
    (pk: any) => pk?.lensPackage?.color && pk?.lensPackage?.color !== ''
  );

  const packages = getCheapestPackages(
    filters?.color ? coloredPackages : currentPackage,
    filters
  );

  if (!packages) return null;

  const { minSelectedPrice, minPackageFilterPrice } = packages;
  const selectedPackage = minSelectedPrice.lensPackage;
  const filteredPackage = minPackageFilterPrice.lensPackage;

  if (
    window.rxcBrain.willManualSelectionBeDone() ||
    window.rxcBrain?.currentStep?.params?.find(
      (p) => p?.key === 'treatmentsFamily' && p?.value === 'Sun'
    ) ||
    // (coloredPackages.length
    //   ? (coloredPackages ?? currentPackage)
    //   : currentPackage
    // ).every(
    //   (cp) =>
    //     cp &&
    //     sunRule(cp.lensPackage) &&
    //     !window.rxcBrain?.stepChain?.find((step: any) =>
    //       step?.params?.find((param: any) => param.key === 'color')
    //     )
    // ) ||
    window.rxcBrain?.currentStep?.key?.toLowerCase() === 'lenscolour' ||
    window.rxcBrain?.currentStep?.key?.toLowerCase() === 'lensbundle'
  ) {
    // steps before treatmentsFamily
    if (showFramePlusLensesPrice === true) {
      if (
        !filteredPackage?.originalListPrice &&
        !filteredPackage?.originalOfferPrice
      ) {
        // showFramePlusLenses with no bundles
        return {
          price:
            parseFloat(filteredPackage?.listPrice) +
            parseFloat(minPackageFilterPrice?.frame?.offerPrice),
          discount:
            parseFloat(filteredPackage?.listPrice) -
            parseFloat(filteredPackage?.offerPrice),
          insPrice: parseFloat(filteredPackage?.insPrice),
        };
      } else if (
        filteredPackage?.originalListPrice &&
        filteredPackage?.originalOfferPrice &&
        filteredPackage?.listPrice === filteredPackage?.offerPrice
      ) {
        //if no promo applied to bundles
        //(listPrice and offerPrice refer to the bundle price, originalListPrice and originalOfferPrice refer to lensPackage prices )
        return {
          price:
            parseFloat(filteredPackage?.originalListPrice) +
            parseFloat(minPackageFilterPrice?.frame?.offerPrice),
          discount:
            parseFloat(filteredPackage?.originalListPrice) +
            parseFloat(minPackageFilterPrice?.frame?.offerPrice) -
            parseFloat(filteredPackage?.listPrice),
          insPrice: parseFloat(filteredPackage?.insPrice),
        };
      } else {
        return {
          price: parseFloat(filteredPackage?.listPrice),
          discount:
            parseFloat(filteredPackage?.listPrice) -
            parseFloat(filteredPackage?.offerPrice),
          insPrice: parseFloat(filteredPackage?.insPrice),
        };
      }
    } else {
      return {
        price: parseFloat(filteredPackage?.listPrice),
        discount:
          parseFloat(filteredPackage?.listPrice) -
          parseFloat(filteredPackage?.offerPrice),
        insPrice: parseFloat(filteredPackage?.insPrice),
      };
    }
  } else {
    const deltaPrice =
      parseFloat(filteredPackage?.listPrice) -
      parseFloat(selectedPackage?.listPrice);
    const deltaOfferPrice =
      parseFloat(filteredPackage?.offerPrice) +
      (isGVP ? parseFloat(minPackageFilterPrice.frame?.offerPrice) : 0) -
      (parseFloat(selectedPackage?.offerPrice) +
        (isGVP ? parseFloat(minSelectedPrice.frame.offerPrice) : 0));

    if (deltaPrice < 0) {
      return {
        price: parseFloat(selectedPackage?.listPrice),
        discount:
          parseFloat(selectedPackage?.listPrice) -
          parseFloat(selectedPackage?.offerPrice),
        insPrice:
          parseFloat(filteredPackage?.insPrice) -
          parseFloat(selectedPackage?.insPrice),
      };
    } else {
      const price = deltaOfferPrice > deltaPrice ? deltaOfferPrice : deltaPrice;
      const discount =
        deltaOfferPrice > deltaPrice ? 0 : deltaPrice - deltaOfferPrice;

      return {
        price,
        discount,
        insPrice:
          parseFloat(filteredPackage?.insPrice) -
          parseFloat(selectedPackage?.insPrice),
      };
    }
  }
};

export const getPriceFromPackageAndCustomFiltered = (
  currentPackage: any[],
  filtered: any[]
) => {
  if (currentPackage) {
    if (!filtered || !filtered.length) {
      return null;
    }
    const minSelectedPrice = getCheapPackageFromList(currentPackage);
    const minPackageFilterPrice = getCheapPackageFromList(filtered);

    console.log('###', minSelectedPrice, minPackageFilterPrice);

    // DAN WIP (this isn't called for some reason)
    // if (workflow.willManualSelectionBeDone()) {
    //   return {
    //     price: parseFloat(minPackageFilterPrice.lensPackage?.listPrice),
    //     discount:
    //       parseFloat(minPackageFilterPrice.lensPackage?.listPrice) -
    //       parseFloat(minPackageFilterPrice.lensPackage?.offerPrice),
    //     insPrice:
    //       minPackageFilterPrice.lensPackage?.insPrice == null
    //         ? null
    //         : parseFloat(minPackageFilterPrice.lensPackage?.insPrice),
    //   };
    // }

    //TODO: DA RIVEDERE

    //Controllo se abbiamo già selezionate delle lenti, sennò devo mostarare il prezzo intero
    if (!window.rxcBrain?.currentPrices?.lens?.price) {
      return {
        price: parseFloat(minPackageFilterPrice.lensPackage?.listPrice),
        discount:
          parseFloat(minPackageFilterPrice.lensPackage?.listPrice) -
          parseFloat(minPackageFilterPrice.lensPackage?.offerPrice),
        insPrice: parseFloat(minPackageFilterPrice.lensPackage?.insPrice),
      };
    }

    const deltaPrice =
      parseFloat(minPackageFilterPrice.lensPackage?.listPrice) -
      parseFloat(minSelectedPrice.lensPackage?.listPrice);

    const deltaOfferPrice =
      parseFloat(minPackageFilterPrice.lensPackage?.offerPrice) -
      parseFloat(minSelectedPrice.lensPackage?.offerPrice);
    if (deltaPrice < 0) {
      return {
        price: parseFloat(minPackageFilterPrice.lensPackage?.listPrice),
        discount:
          parseFloat(minPackageFilterPrice.lensPackage?.listPrice) -
          parseFloat(minPackageFilterPrice.lensPackage?.offerPrice),
        insPrice:
          minPackageFilterPrice.lensPackage?.insPrice == null
            ? null
            : parseFloat(minPackageFilterPrice.lensPackage?.insPrice),
      };
    } else {
      return {
        price: deltaPrice,
        discount: deltaPrice - deltaOfferPrice,
        insPrice:
          minPackageFilterPrice.lensPackage?.insPrice == null
            ? null
            : parseFloat(minPackageFilterPrice.lensPackage?.insPrice) -
              parseFloat(minSelectedPrice.lensPackage?.insPrice),
      };
    }
  }
  return {
    price: 0,
    discount: 0,
    insPrice: 0,
  };
};

export const getPriceStep = (
  filters: Record<string, any>,
  currentPackages?: LensPackage[]
) => {
  const isGVP = checkGVP(currentPackages);
  // TO DO
  const showFramePlusLensesPrice = window.rxcBrain.showFramePlusLensesPrice;

  return getPriceFromPackageAndFiltered(
    currentPackages,
    filters,
    isGVP,
    showFramePlusLensesPrice
  );
};
